import React from 'react'
import { AuthProvider, PlacesLoader } from '@/loaders'
import { store as defaultStore } from '@/redux/store/initStore'
import { StytchB2BProvider } from '@stytch/react/b2b'
import { StytchB2BUIClient } from '@stytch/vanilla-js/b2b'
import { SnackbarProvider } from 'notistack'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'

import { TanStackQueryProvider } from './TanStackQuery'
import { ThemeProvider } from './Theme'
import { ChatBubbleProvider } from '@/components/Inbox/ChatBubbleProvider'
import { useAuth } from '@/loaders/Auth'
import { useUserPreferencesQuery } from '@/queries/userPreferences'
import { useAppDispatch } from '@/redux/hooks/useAppState'
import { setTheme } from '@/redux/store/slices/theme/themeSlice'
import { Theme } from '@/theme'

import type { FC, PropsWithChildren, ReactElement } from 'react'

interface PropsWithStore extends PropsWithChildren {
	store?: typeof defaultStore
}

// Create the Stytch client once at module scope to avoid multiple instances
const stytchClient = new StytchB2BUIClient(import.meta.env.VITE_STYTCH_PUBLIC_TOKEN as string)

export const AppProvider: FC<PropsWithStore> = ({ children, store = defaultStore }) => {
	return (
		<Provider store={store}>
			<BrowserRouter>
				<StytchB2BProvider stytch={stytchClient}>
					<AuthProvider>
						<TanStackQueryProvider>
							<PlacesLoader>
								<ThemeProvider>
									<ChatBubbleProvider>
										<ThemeSyncer />
										<SnackbarProvider maxSnack={3}>{children}</SnackbarProvider>
									</ChatBubbleProvider>
								</ThemeProvider>
							</PlacesLoader>
						</TanStackQueryProvider>
					</AuthProvider>
				</StytchB2BProvider>
			</BrowserRouter>
		</Provider>
	) as ReactElement<any, any>
}

// Sync Stytch user preferences theme to Redux theme state
function ThemeSyncer() {
	const { loggedIn } = useAuth()
	const dispatch = useAppDispatch()

	// Query user preferences from Stytch untrusted_metadata
	const preferencesQuery = useUserPreferencesQuery()

	React.useEffect(() => {
		// Only sync when we have a successful authenticated query
		if (!loggedIn || !preferencesQuery.isSuccess || !preferencesQuery.data) return

		const mode = preferencesQuery.data.theme
		const reduxTheme = mode === 'dark' ? Theme.Dark : Theme.Light

		dispatch(setTheme(reduxTheme))
	}, [loggedIn, preferencesQuery.isSuccess, preferencesQuery.data?.theme, dispatch])

	return null
}
